import { useRef, useState } from "react";
import { useMutation } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { DatePicker } from "@/components/design/DatePicker";
import { InlineEdit } from "@/components/design/InlineEdit";
import { ListingReadershipField } from "./ListingReadershipField";
import { useValidationFallback } from "@/components/match/use-match-editor";
import { UpdateListingDocument, type UpdateListingDataInput } from "@/graphql/generated/operations";
import { formatDateTime } from "@/lib/format-date";
import { userErrorMessage } from "@/lib/user-error";
import { ListingField } from "./ListingField";
import { fromLocalDateTimeInput, MAX_RETENTION_DAYS, parseRetentionDays, toLocalDateTimeInput, throwIfRejected, ValidationRejected } from "./listing-form";
import { forgetListings, type ListingDetail } from "./use-listing";

export function ListingConfigurationField({ listing, section, label }: {
  listing: ListingDetail; section: "deadline" | "retention" | "readership"; label: string;
}) {
  if (section === "readership") return <ListingReadershipField listing={listing} label={label} />;
  return <ConfigurationValue listing={listing} section={section} label={label} />;
}

function ConfigurationValue({ listing, section, label }: {
  listing: ListingDetail; section: "deadline" | "retention"; label: string;
}) {
  const intl = useIntl();
  const fallback = useValidationFallback();
  const [updateListing] = useMutation(UpdateListingDocument);
  const [saving, setSaving] = useState(false);
  const pending = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [deadline, setDeadline] = useState(toLocalDateTimeInput(listing.deadline));
  const [retention, setRetention] = useState(String(listing.retentionDays ?? ""));
  const canEdit = listing.actions.canUpdate;

  async function save(data: UpdateListingDataInput) {
    if (!canEdit || pending.current) return;
    pending.current = true; setSaving(true); setError(null);
    try {
      const result = await updateListing({ variables: { id: listing.id, input: { data, dryRun: false } }, update: forgetListings });
      throwIfRejected(result.data?.updateListing, fallback);
    } catch (e) {
      setDeadline(toLocalDateTimeInput(listing.deadline));
      setRetention(String(listing.retentionDays ?? ""));
      setError(e instanceof ValidationRejected ? e.message : userErrorMessage(intl, e));
    }
    finally { pending.current = false; setSaving(false); }
  }
  function commitRetention(value: string) {
    const days = parseRetentionDays(value);
    if (typeof days !== "number") {
      setError(intl.formatMessage({ id: "share.listings.form.retentionInvalid" })); return;
    }
    setError(null);
    if (days !== listing.retentionDays) void save({ retentionDays: days });
  }
  return <ListingField label={label}>
    <fieldset disabled={saving} className="min-w-0">
      {section === "deadline" ? canEdit ? <DatePicker id="listing-form-deadline" aria-label={label}
        mode="datetime-local" value={deadline} min={toLocalDateTimeInput(new Date().toISOString())}
        placeholder={intl.formatMessage({ id: "share.listings.noDeadline" })}
        onChange={value => {
          const next = fromLocalDateTimeInput(value);
          if (value && (!next || new Date(next).getTime() <= Date.now())) {
            setError(intl.formatMessage({ id: "share.listings.deadlineDialog.future" }));
            return;
          }
          setDeadline(value); void save({ deadline: next });
        }} />
        : formatDateTime(listing.deadline, intl.locale) || <FormattedMessage id="share.listings.noDeadline" /> : null}
      {section === "retention" ? <div className="flex flex-wrap items-baseline gap-2">
        <InlineEdit id="listing-form-retention" ariaLabel={label} type="number" min={1} max={MAX_RETENTION_DAYS}
          value={retention} onChange={setRetention} onCommit={commitRetention} editable={canEdit} clearable={false} />
        <span><FormattedMessage id="share.listings.retentionUnit" /></span>
      </div> : null}

    </fieldset>
    {saving ? <p role="status" className="smycles-field-meta mt-1"><FormattedMessage id="common.saving" /></p> : null}
    {error ? <p role="alert" className="smycles-field-meta mt-1">{error}</p> : null}
  </ListingField>;
}
